import React, { useEffect, useState } from "react";
import { useBodyMeasurementServiceContext } from "../../../../contexts/BodyMeasurementServiceContext";
import { MeasurementResult } from "../../../../utilities/types/measurement-data/types";
import { SizeGuideRequest } from "../../../../utilities/types/measurement-data/types";
import Loader from "../../../loading/Loader";
import MainButton from "../../../button/MainButton";
import SizeGuideResult from "./SizeGuideResult";

export interface QuestionnaireStep {
  brand: string;
  gender: string;
  category: string;
  type: string;
  sizeType: string;
}

export type SizeGuideData = SizeGuideRequest;

const SizeGuideQuestionnaire: React.FC = () => {
  const { availableCharts, fetchAvailableCharts, submitSizeGuide, measurementData, loadingSizeGuide, sizeGuideResult, resetSizeGuide, error } = useBodyMeasurementServiceContext();

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [selections, setSelections] = useState<QuestionnaireStep>({
    brand: "",
    gender: "",
    category: "",
    type: "",
    sizeType: "",
  });

  const [hasSizeTypes, setHasSizeTypes] = useState(false);

  const [types, setTypes] = useState<string[]>([]);
  const [sizeTypes, setSizeTypes] = useState<string[]>([]);

  useEffect(() => {
    fetchAvailableCharts();
  }, []);

  useEffect(() => {
    if (sizeGuideResult === null) {
      setCurrentStep(0);
      setSelectedItem("");

      setHasSizeTypes(false);
      setTypes([]);
      setSizeTypes([]);
      setSelections({
        brand: "",
        gender: "",
        category: "",
        type: "",
        sizeType: "",
      });
    }
  }, [sizeGuideResult]);

  useEffect(() => {
    if (!availableCharts) return;

    let newTypes: string[] = [];
    let hasTypes = false;

    if (selections.brand && selections.gender && selections.category) {
      const categoryData = availableCharts[selections.brand]?.[selections.gender]?.[selections.category];
      if (categoryData) {
        newTypes = Object.keys(categoryData);

        const firstType = newTypes[0];
        if (firstType && categoryData[firstType] && typeof categoryData[firstType] === "object") {
          const sizeTypeKeys = Object.keys(categoryData[firstType]);
          hasTypes = sizeTypeKeys.some((key) => key === "Regular" || key === "PlusSize" || key === "PLusSize");
        }
      }
    }

    setTypes(newTypes);
    setHasSizeTypes(hasTypes);
  }, [availableCharts, selections.brand, selections.gender, selections.category]);

  useEffect(() => {
    if (!availableCharts || !selections.type) {
      setSizeTypes([]);
      return;
    }

    let newSizeTypes: string[] = [];

    if (selections.brand && selections.gender && selections.category && selections.type) {
      const typeData = availableCharts[selections.brand]?.[selections.gender]?.[selections.category]?.[selections.type];

      if (typeData && typeof typeData === "object") {
        newSizeTypes = Object.keys(typeData);
        setHasSizeTypes(newSizeTypes.length > 0);
      }
    }

    setSizeTypes(newSizeTypes);
  }, [availableCharts, selections.type, selections.brand, selections.gender, selections.category]);

  const handleFullReset = () => {
    resetSizeGuide();
    setCurrentStep(0);

    setHasSizeTypes(false);
    setTypes([]);
    setSizeTypes([]);
    setSelectedItem("");
    setSelections({
      brand: "",
      gender: "",
      category: "",
      type: "",
      sizeType: "",
    });
  };

  if (!availableCharts) return null;

  const brands = Object.keys(availableCharts);
  const genders = selections.brand ? Object.keys(availableCharts[selections.brand]) : [];

  const categories = selections.gender ? Object.keys(availableCharts[selections.brand][selections.gender]) : [];

  const allSteps = [
    {
      key: "brand",
      title: "Brand",
      description: "Choose your preferred brand",
      options: brands,
    },
    {
      key: "gender",
      title: "Gender",
      description: "Select the gender category",
      options: genders,
    },
    {
      key: "category",
      title: "Category",
      description: "Choose product category",
      options: categories,
    },
    {
      key: "type",
      title: "Size",
      description: "Select your preferred size guide by country",
      options: types,
    },
    {
      key: "sizeType",
      title: "Fit",
      description: "Select your preferred fit",
      options: sizeTypes,
    },
  ];

  const steps = (() => {
    let filteredSteps = [...allSteps];

    if (!hasSizeTypes) {
      filteredSteps = filteredSteps.filter((step) => step.key !== "sizeType");
    }

    return filteredSteps;
  })();

  const handleItemSelect = (value: string) => {
    setSelectedItem(value);
  };

  const handleConfirmSelection = async () => {
    if (!selectedItem) return;

    const currentVisualStep = steps[currentStep];
    const key = currentVisualStep.key as keyof QuestionnaireStep;

    const newSelections = { ...selections, [key]: selectedItem };

    if (key === "brand") {
      newSelections.gender = "";
      newSelections.category = "";
      newSelections.type = "";
      newSelections.sizeType = "";

      setSelections(newSelections);

      setCurrentStep(currentStep + 1);
    } else if (key === "gender") {
      newSelections.category = "";
      newSelections.type = "";
      newSelections.sizeType = "";

      setSelections(newSelections);

      setCurrentStep(currentStep + 1);
    } else if (key === "category") {
      newSelections.type = "";
      newSelections.sizeType = "";

      setSelections(newSelections);
      setCurrentStep(currentStep + 1);
    } else if (key === "type") {
      newSelections.sizeType = "";

      setSelections(newSelections);

      if (hasSizeTypes) {
        const typeData = availableCharts[newSelections.brand]?.[newSelections.gender]?.[newSelections.category]?.[selectedItem];
        if (typeData && typeof typeData === "object" && Object.keys(typeData).length > 0) {
          setCurrentStep(currentStep + 1);
          return;
        }
      }

      submitSizeGuideData(newSelections);
    } else if (key === "sizeType") {
      setSelections(newSelections);

      submitSizeGuideData(newSelections);
    }

    setSelectedItem("");
  };

  const submitSizeGuideData = (selectionData: QuestionnaireStep) => {
    const sizeData = measurementData
      ? Object.entries(measurementData)
          .filter(([key]) => !["Details", "MetaData"].includes(key))
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
      : {};

    const keyList = [];
    keyList.push(selectionData.brand);
    keyList.push(selectionData.gender);

    keyList.push(selectionData.category);

    keyList.push(selectionData.type);

    if (hasSizeTypes && selectionData.sizeType) {
      keyList.push(selectionData.sizeType);
    }

    const sizeGuideData: SizeGuideData = {
      KeyList: keyList,
      Size: sizeData,
    };

    setCurrentStep(steps.length);
    submitSizeGuide(sizeGuideData);
  };

  const handlePrevStep = () => {
    if (sizeGuideResult) {
      handleFullReset();
      return;
    }

    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setSelectedItem("");
    }
  };

  const renderStepContent = (step: (typeof steps)[0]) => (
    <div className="grid grid-cols-2 gap-4">
      {step.options.map((option: any) => (
        <button
          key={option}
          onClick={() => handleItemSelect(option)}
          className={`p-4 transition-colors border rounded-lg 
            ${selectedItem === option ? "border-gorgonzola-blue-300 bg-gorgonzola-blue-50" : "hover:bg-gray-50"}`}
        >
          {option}
        </button>
      ))}
    </div>
  );

  const displaySteps = [...steps, { title: "Results", key: "results" }];

  const normalizedGender = selections.gender ? selections.gender.toLowerCase() : "genderfree";

  const getGridColsClass = (stepsCount: number) => {
    switch (stepsCount) {
      case 1:
        return "grid-cols-1";
      case 2:
        return "grid-cols-2";
      case 3:
        return "grid-cols-3";
      case 4:
        return "grid-cols-4";
      case 5:
        return "grid-cols-5";
      case 6:
        return "grid-cols-6";
      default:
        return "grid-cols-5";
    }
  };

  const gridColsClass = getGridColsClass(displaySteps.length);

  return (
    <div className={`flex flex-col w-full gap-4 md:gap-6 md:p-6 lg:p-8 ${!sizeGuideResult ? "max-w-[40rem]" : ""}`}>
      {!sizeGuideResult ? (
        <div className={`relative grid gap-2 ${gridColsClass} *:flex-grow`}>
          {displaySteps.map((step, index) => (
            <div key={index} className="relative flex flex-col items-center">
              {index < displaySteps.length - 1 && (
                <div className={`block absolute top-5 left-full -translate-x-1/2 w-full h-[2px] ${index < currentStep ? "bg-gorgonzola-blue-300" : "bg-closed-shutter-200"}`} />
              )}

              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all duration-600 bg-white z-20 
                ${index <= currentStep ? "border-gorgonzola-blue-300 bg-gorgonzola-blue-50" : "border-closed-shutter-200"}`}
              >
                {index + 1}
              </div>
              <span
                className={`hidden md:block mt-3.25 text-xs font-inter-medium 
                ${index <= currentStep ? "text-gorgonzola-blue-300" : "text-closed-shutter-200"}`}
              >
                {step.title}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {currentStep < steps.length ? (
        <div className="space-y-2">
          <h2 className="text-lg font-inter-bold text-closed-shutter-300">{steps[currentStep].title}</h2>
          <p className="text-base font-inter-regular text-closed-shutter-200">{steps[currentStep].description}</p>
          {renderStepContent(steps[currentStep])}
        </div>
      ) : loadingSizeGuide ? (
        <div className="flex justify-center p-8">
          <Loader />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-4 p-4">
          <h2 className="text-center text-closed-shutter-300">This size guide is not available at the moment. Please try another option.</h2>
          <MainButton type="secondary" onClick={handleFullReset}>
            Try Different Options
          </MainButton>
        </div>
      ) : null}

      {sizeGuideResult && (
        <>
          <SizeGuideResult results={sizeGuideResult} gender={normalizedGender as "men" | "women" | "genderfree"} />

          <div className="flex justify-center mt-4 md:mt-6">
            <MainButton type="secondary" onClick={handleFullReset}>
              Start Over
            </MainButton>
          </div>
        </>
      )}

      {!error && !sizeGuideResult ? (
        <div className={`flex justify-between mt-2 md:mt-4 lg:mt-6`}>
          <MainButton type="secondary" onClick={handlePrevStep} disabled={currentStep <= 0}>
            Previous
          </MainButton>

          {currentStep < steps.length && (
            <MainButton type="primary" onClick={handleConfirmSelection} disabled={!selectedItem}>
              {currentStep === steps.length - 1 ? "Show Results" : "Next"}
            </MainButton>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default SizeGuideQuestionnaire;
